
import { useState, useEffect } from 'react';

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  itemPrice: string;
}

export default function OrderModal({ isOpen, onClose, itemName, itemPrice }: OrderModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    whatsapp: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Delivery address is required';
    }

    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = 'WhatsApp number is required';
    } else if (!/^[0-9+\s-()]+$/.test(formData.whatsapp)) {
      newErrors.whatsapp = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit form data
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('item_name', itemName);
      formDataToSubmit.append('item_price', itemPrice);
      formDataToSubmit.append('customer_name', formData.name);
      formDataToSubmit.append('delivery_address', formData.address);
      formDataToSubmit.append('whatsapp_number', formData.whatsapp);
      formDataToSubmit.append('order_notes', formData.notes || 'No special notes');

      await fetch('https://readdy.ai/api/form/d4rc0bt1jqpb85ajp440', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      // Create WhatsApp message
      const message = `Hello EatSpot! I would like to order:

📦 *Order Details:*
Item: ${itemName}
Price: ${itemPrice}

👤 *Customer Information:*
Name: ${formData.name}
Delivery Address: ${formData.address}
WhatsApp: ${formData.whatsapp}

📝 *Special Notes:*
${formData.notes || 'None'}

Please confirm my order. Thank you!`;

      const whatsappUrl = `https://wa.me/923140607452?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      // Reset form and close modal
      setFormData({ name: '', address: '', whatsapp: '', notes: '' });
      onClose();
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('There was an error processing your order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1">Complete Your Order</h2>
              <p className="text-orange-100 text-sm">Fill in your details below</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-amber-50 border-l-4 border-orange-500 p-4 m-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">You're ordering:</p>
              <h3 className="text-xl font-bold text-amber-900">{itemName}</h3>
            </div>
            <div className="text-2xl font-bold text-orange-500">{itemPrice}</div>
          </div>
        </div>

        {/* Form */}
        <form id="order-form" data-readdy-form onSubmit={handleSubmit} className="p-6 pt-0 space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-user-line text-lg"></i>
              </div>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all ${
                  errors.name ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter your full name"
              />
            </div>
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
              Delivery Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-3 w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all ${
                  errors.address ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="e.g., F-7 Markaz, Islamabad"
              />
            </div>
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
          </div>

          {/* WhatsApp Number */}
          <div>
            <label htmlFor="whatsapp" className="block text-sm font-semibold text-gray-700 mb-2">
              WhatsApp Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-whatsapp-line text-lg"></i>
              </div>
              <input
                type="tel"
                id="whatsapp"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all ${
                  errors.whatsapp ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="+92 314 0607452"
              />
            </div>
            {errors.whatsapp && <p className="text-red-500 text-xs mt-1">{errors.whatsapp}</p>}
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
              Special Instructions (Optional)
            </label>
            <div className="relative">
              <div className="absolute left-3 top-3 w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-file-text-line text-lg"></i>
              </div>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                maxLength={500}
                rows={3}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all resize-none"
                placeholder="Any special requests? (e.g., extra spicy, no onions)"
              ></textarea>
            </div>
            <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500 characters</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line text-xl animate-spin"></i>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <i className="ri-whatsapp-fill text-2xl"></i>
                <span>Confirm Order on WhatsApp</span>
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-500 mt-4">
            By placing this order, you'll be redirected to WhatsApp to complete your purchase
          </p>
        </form>
      </div>
    </div>
  );
}
